import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import { Formik, Form, Field, ErrorMessage } from "formik";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import { CloseOutlined } from "@mui/icons-material";
import api from "API/api";
import MDBox from "components/MDBox";
import colors from "assets/theme/base/colors";
import * as Yup from "yup";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const EditUserProfile = ({
  editProfileOpen,
  setEditProfileOpen,
  currentUser = null,
}) => {
  const navigate = useNavigate();
  const { white, gradients } = colors;
  const isEdit = Boolean(currentUser);
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const [loading, setLoading] = useState(false);

  const initialValues = {
    username: user?.username || "",
    firstname: user?.firstname || "",
    lastname: user?.lastname || "",
    new_email: user?.email || "",
    phone: user?.phone || "",
    role: user?.role || "",
    // site_id: user?.site_id || "",
  };

  const validationSchema = isEdit
    ? null
    : Yup.object().shape({
        username: Yup.string()
          .required("User name is required")
          .min(2, "User name must be at least 2 characters long"),
        firstname: Yup.string()
          .required("First name is required")
          .min(2, "First name must be at least 2 characters long"),
        lastname: Yup.string()
          .required("Last name is required")
          .min(2, "Last name must be at least 2 characters long"),
        new_email: Yup.string()
          .email("Invalid email address")
          .required("Email is required"),
        phone: Yup.string()
          .matches(/^[0-9]+$/, "Phone number must be only digits")
          .min(10, "Phone number must be at least 10 digits long")
          .required("Phone number is required"),
        role: Yup.string().required("Please Select a role"),
      });

  const handleSubmit = async (values, actions) => {
    const formData = new FormData();
    Object.keys(values).forEach((key) => formData.append(key, values[key]));
    setLoading(true);
    try {
      const response = await api.put(`/userapi`, formData);
      toast.success(response.data.message);
      setEditProfileOpen(false);
      localStorage.setItem("user", JSON.stringify(response?.data?.data));
      actions.resetForm();
      if (
        response?.data?.message.includes(
          "A new verification email has been sent"
        )
      ) {
        navigate("/verify-email");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message);
    } finally {
      setLoading(false);
      actions.setSubmitting(false);
    }
  };

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: "14px",
    outline: "none !important",
  };

  return (
    <Modal open={editProfileOpen} onClose={() => setEditProfileOpen(false)}>
      <Box sx={style} className="add-user-modal">
        <Formik
          initialValues={initialValues}
          validationSchema={!isEdit ? validationSchema : undefined}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting, dirty }) => (
            <Form>
              <Box
                sx={{
                  mb: 3,
                  backgroundColor: gradients.info.main,
                  borderTopLeftRadius: "14px",
                  borderTopRightRadius: "14px",
                }}
              >
                <Box
                  sx={{
                    padding: "15px",
                    display: "flex",
                    justifyContent: "space-between",
                    color: white.main,
                  }}
                >
                  <Typography sx={{ fontSize: "18px", fontWeight: "500" }}>
                    EDIT USER
                  </Typography>
                  <CloseOutlined
                    onClick={() => setEditProfileOpen(false)}
                    sx={{ cursor: "pointer", fontSize: "30px", mt: "5px" }}
                  />
                </Box>
              </Box>
              <Box sx={{ padding: "15px" }}>
                <Box mb={2}>
                  <Field
                    as={MDInput}
                    name="username"
                    placeholder="User Name"
                    fullWidth
                    autoComplete="off"
                  />
                  <ErrorMessage
                    name="username"
                    component={MDTypography}
                    variant="caption"
                    color="error"
                  />
                </Box>
                <Box mb={2}>
                  <Field
                    as={MDInput}
                    name="firstname"
                    placeholder="First Name"
                    fullWidth
                    autoComplete="off"
                  />
                  <ErrorMessage
                    name="firstname"
                    component={MDTypography}
                    variant="caption"
                    color="error"
                  />
                </Box>
                <Box mb={2}>
                  <Field
                    as={MDInput}
                    name="lastname"
                    placeholder="Last Name"
                    fullWidth
                    autoComplete="off"
                  />
                  <ErrorMessage
                    name="lastname"
                    component={MDTypography}
                    variant="caption"
                    color="error"
                  />
                </Box>
                <Box mb={2}>
                  <Field
                    as={MDInput}
                    name="new_email"
                    placeholder="Email"
                    fullWidth
                    autoComplete="off"
                  />
                  <ErrorMessage
                    name="email"
                    component={MDTypography}
                    variant="caption"
                    color="error"
                  />
                </Box>
                <Box mb={2}>
                  <Field
                    as={MDInput}
                    name="phone"
                    placeholder="Phone"
                    fullWidth
                    autoComplete="off"
                  />
                  <ErrorMessage
                    name="phone"
                    component={MDTypography}
                    variant="caption"
                    color="error"
                  />
                </Box>
                <MDBox mt={4} mb={1}>
                  <Button
                    type="submit"
                    sx={{
                      width: "100%",
                      height: "38px",
                      fontSize: "14px",
                      fontWeight: "500",
                      background: !dirty
                        ? "#e0e0e0"
                        : `${gradients.info.state} `,
                      color: white.main,
                      border: "none",
                      marginRight: "0",
                      marginBottom: "0",
                      "&:hover": {
                        background: `${gradients.info.state} !important`,
                        color: white.main,
                      },
                    }}
                    disabled={loading || !dirty}
                  >
                    {loading ? (
                      <CircularProgress
                        size={25}
                        sx={{ color: "white !important" }}
                      />
                    ) : (
                      "UPDATE"
                    )}
                  </Button>
                </MDBox>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default EditUserProfile;
